/**
 * What a company may read about a citizen. (W2.5 · F31 Phase 2)
 *
 * The MODEL and its vocabulary. Enforcement is W3 — the contract's owner
 * defines it, consumers come later (principle P4).
 *
 * ── Why a `text[]` and not an enum ───────────────────────────────────────
 *
 * A new hero must not need a migration to become consentable. The column is
 * `text[]`; this list is the vocabulary the API validates against, so an
 * unknown scope is rejected at the edge rather than silently stored and
 * silently ignored by every reader.
 *
 * ── Two tiers, one record ────────────────────────────────────────────────
 *
 * JOURNEY-SCOPED data (CheckUp, TelePortal, QultureUp, Care, the IRR feed)
 * lives on the journey. TALENT-SCOPED data — TestIt results, earned stamps,
 * the pitch video, the CV, household members, Care check-ins — belongs to the
 * person. Both tiers are granted on the same relationship row
 * (`journey_engagements.consent_scopes`), per company and journey.
 *
 * ── What a scope withholds ───────────────────────────────────────────────
 *
 * A scope withholds the citizen's RESULTS on that hero — a CheckUp score, badge
 * and MESH; TelePortal track progress and the citizen's own tasks and
 * documents; QultureUp progress and level; the Commitment Index, pillar scores,
 * objectives and feedback; TestIt results; stamps and miles; the household —
 * from every company read: per-talent values AND the averages, distributions
 * and counts built from them (a withheld journey is counted as withheld, never
 * averaged in). It does NOT withhold the company's own pipeline: that a CheckUp
 * it asked for is in progress and its steps, the TestIt tallies of the tests it
 * invited, its own decisions, and the company-owned tasks in the relocation kit
 * (its obligations to the citizen).
 *
 * For the CheckUp the line is drawn in ONE function (D4, 4 Oct 2026 — without
 * consent: process yes, results no): `companyCheckup` in
 * `apps/platform-api/src/modules/business/engagements/company-checkup.ts`.
 * Every company read of a CheckUp goes through it, and it also says WHY a
 * result is not on screen — never shared (`awaiting_consent`) or shared and
 * taken back (`withdrawn`).
 *
 * Enforced by one SQL predicate for aggregates
 * (`BusinessJourneyAccessService.consentedJourneyIdsSubquery`) and one batched
 * resolver for rows (`JourneyEngagementConsentService.entitledByJourney`), held
 * to the same answer by a parity test; `consent-leak-matrix.integration.spec`
 * holds every company read to it.
 */

/** Data reachable through the journey, and therefore through the engagement. */
export const JOURNEY_CONSENT_SCOPES = [
  'checkup',
  'teleportal',
  'qultureup',
  'care',
  'irr',
] as const;

/**
 * Data that belongs to the TALENT, not to any one journey. These have no scope
 * beyond pipeline membership today, which is the gap W3 closes.
 */
export const TALENT_CONSENT_SCOPES = [
  'testit',
  'passport_stamps',
  'pitch_video',
  'cv',
  'household',
  'care_checkins',
] as const;

export const CONSENT_SCOPES = [...JOURNEY_CONSENT_SCOPES, ...TALENT_CONSENT_SCOPES] as const;

export type JourneyConsentScope = (typeof JOURNEY_CONSENT_SCOPES)[number];
export type TalentConsentScope = (typeof TALENT_CONSENT_SCOPES)[number];
export type ConsentScope = (typeof CONSENT_SCOPES)[number];

const CONSENT_SCOPE_SET: ReadonlySet<string> = new Set(CONSENT_SCOPES);

/** Is this a scope the platform knows about? Used to reject unknown input. */
export function isConsentScope(value: string): value is ConsentScope {
  return CONSENT_SCOPE_SET.has(value);
}

/**
 * The default scopes for a new engagement, by invitation kind. (D-17)
 *
 * THE DEFAULT IS TODAY'S ACCESS, EXACTLY. W2.5 ships the model and the write
 * API; it must not change who can see what, or a consent model becomes an
 * access-control change nobody reviewed. So the defaults reproduce what
 * `resolveEntitledScope` already grants from `invitation_kind`:
 *
 *   • a `full_checkup` invitation is a company inviting someone onto a
 *     relocation journey, and it can already read every journey-scoped hero
 *     plus the talent-scoped evidence the pipeline surfaces;
 *   • a `testit` invitation is narrower BY CONSTRUCTION — that resolver
 *     already returns only `testitTests`, and nothing else is rendered for it.
 *
 * Narrowing from here is W3's enforcement work with real product decisions
 * behind it. Widening is the CITIZEN's answer to a request (D2, 4 Oct 2026 —
 * FR-7 re-opened): a company that asks an accepted talent for more than the
 * relationship's consent covers sends a new request (`shareRequestFor` in
 * `./relationship-ask`), and only the citizen's Share writes the wider scopes,
 * on the trail as `request_accept`. An accepted invitation is never re-scoped
 * in place. The consent TEXT is a W8
 * decision with counsel and is deliberately not invented here —
 * `consent_text_version` records which wording was shown, and a row written
 * before there is any wording records exactly that.
 */
export const DEFAULT_CONSENT_SCOPES: Readonly<Record<'full_checkup' | 'testit', ConsentScope[]>> = {
  full_checkup: [...CONSENT_SCOPES],
  testit: ['testit'],
};

/**
 * The scopes a relationship is born with, from the invitation the citizen
 * accepted (4 Oct 2026). A `testit` invitation that asks for the PITCH carries
 * `pitch_video` too: the recording is the pitch test's result, and without the
 * scope a company that asked for it — and a citizen who accepted that ask —
 * could never play it.
 */
export function defaultConsentScopesFor(
  invitationKind: keyof typeof DEFAULT_CONSENT_SCOPES,
  testitTests: readonly string[]
): ConsentScope[] {
  const scopes = [...DEFAULT_CONSENT_SCOPES[invitationKind]];
  if (invitationKind === 'testit' && testitTests.includes('pitch')) scopes.push('pitch_video');
  return scopes;
}

/**
 * How the consent came to be. `invitation_accept` is the implicit grant that
 * every engagement predating the consent model carries — recorded as what it
 * is rather than backdated into something it was not.
 *
 * `direct_relationship` is the same honesty applied to the other branch: a
 * company that owns the journey outright (a talent in its own pipeline) has no
 * invitation anyone accepted, so calling it `invitation_accept` would record an
 * event that never happened, and calling it `backfill` would claim a migration
 * wrote it. Migration 0115 gave those rows the `full_checkup` scope set and had
 * only `backfill` available to say why; a live write has this.
 */
export const CONSENT_SOURCES = [
  'invitation_accept',
  'direct_relationship',
  'explicit_grant',
  // D2 (4 Oct 2026) — the citizen answered a company's request for more with
  // Share: the widening is theirs, written on the trail with them as the actor.
  'request_accept',
  'citizen_revoke',
  'backfill',
  // Citizen first run · D-1 — the citizen accepted with `start_fresh`, and the
  // automatic grant-back once a CheckUp is scored after that acceptance.
  'citizen_start_fresh',
  'checkup_rescored',
] as const;

/**
 * The scopes a `start_fresh` acceptance withholds: everything a CheckUp scored
 * BEFORE the acceptance would show a company — the result itself, the TestIt
 * results it was built from, and the pitch recording. Granted back together
 * when a CheckUp is scored after the acceptance.
 */
export const FRESH_START_WITHHELD_SCOPES: readonly ConsentScope[] = [
  'checkup',
  'testit',
  'pitch_video',
];

export type ConsentSource = (typeof CONSENT_SOURCES)[number];
